/**
 * `import()` type syntax rather than `import type { Faro }`: the repo is on
 * Prettier 1.19, which can't parse `import type` at all. Either way the
 * reference is erased at compile time, which is the point — see
 * `initTelemetry` for why the SDK must never be pulled in statically.
 */
type Faro = import('@grafana/faro-web-sdk').Faro;

let faroInstance: Faro | undefined;
let initializing = false;

/**
 * Calls made before the SDK finished loading.
 *
 * The New Relic Browser agent this replaced was a blocking `<script>` in
 * `<head>` (`public/newlearning.js`), so `window.newrelic` existed before any
 * React code ran and `NewRelic()?.` never actually short-circuited. Faro loads
 * via dynamic `import()` instead, so there is a real window — hundreds of
 * milliseconds on a cold cache — where `getFaro()` is undefined while the app
 * is already identifying the user and firing events.
 *
 * Without this queue those calls silently no-op. The worst case is
 * `hooks/useIdentifyMetrics.tsx`: for a returning user whose access badge is
 * already hydrated, its ten `setUserId`/`setCustomAttribute` calls all fire in
 * that window, so the session would ship with no `uid`, `plan`,
 * `organizationId` or `districtId` at all — exactly the dimensions the
 * dashboards group by.
 *
 * Replayed in call order, so an identify that precedes an event still wins.
 */
type PendingCall = (faro: Faro) => void;

/**
 * Bounds the damage if the SDK never loads at all (blocked by a content
 * filter, offline, chunk 404 after a deploy). Generous enough to cover the
 * identify burst plus early events, small enough to never be a leak.
 */
const MAX_PENDING_CALLS = 50;

let pendingCalls: PendingCall[] = [];

/**
 * Runs `call` now if Faro is up, otherwise queues it for `drainPendingCalls`.
 */
export const whenTelemetryReady = (call: PendingCall): void => {
  if (faroInstance) {
    call(faroInstance);
    return;
  }
  if (pendingCalls.length >= MAX_PENDING_CALLS) return;
  pendingCalls.push(call);
};

const drainPendingCalls = (faro: Faro) => {
  const queued = pendingCalls;
  pendingCalls = [];
  queued.forEach(call => {
    try {
      call(faro);
    } catch {
      // One bad replayed call must not stop the rest.
    }
  });
};

/**
 * Two separate Grafana Cloud Frontend Observability app registrations —
 * production ("curipod", appId 2026) and everything else ("curipod-staging",
 * appId 2027) — so the two environments' data and dashboards stay fully
 * separate rather than being merely filterable by `app.environment` within
 * one app. Same split curi.live uses.
 *
 * These two strings are load-bearing and must be exact: production has no
 * hyphen, staging does.
 *
 * Must exactly match the `--app-name` (and `--app-id`) passed to
 * `faro-cli inject-bundle-id` / `upload` for the corresponding environment
 * in .github/workflows/deploy-{dev,production}.yml. Faro auto-detects the
 * build's bundle id by reading a `__faroBundleId_<appName>` global that
 * `inject-bundle-id` sets on the built JS files — a mismatch here means
 * source maps silently never correlate to errors.
 */
const getFaroAppName = () =>
  process.env.NEXT_PUBLIC_APP_ENV === 'prod' ? 'curipod' : 'curipod-staging';

/**
 * Deliberately a small, stable set rather than `utils/client`'s
 * `getEnvironmentLabel()`, which returns a per-PR subdomain slug — that would
 * make `deployment_environment` a high-cardinality Loki label.
 */
const getFaroEnvironment = () => {
  switch (process.env.NEXT_PUBLIC_APP_ENV) {
    case 'prod':
      return 'production';
    case 'dev':
      return 'development';
    case 'pr':
      return 'pr';
    default:
      return 'local';
  }
};

/**
 * Faro's `url` needs to be absolute, and must always be this app's own
 * origin — the browser never talks to the Grafana collector directly. Same
 * reasoning as the Mixpanel proxy at `/api/stats/*` and the Unleash proxy at
 * `/api/ff/proxy`: avoid ad-blockers and school content filters, and don't
 * expose the vendor in the URL.
 */
const getCollectorProxyUrl = () => `${window.location.origin}/api/tel/proxy`;

/**
 * Known-noisy error patterns that aren't worth spending the collector's rate
 * budget on (Faro's own `ignoreErrors` — no need to hand-roll a denylist).
 */
const IGNORED_ERROR_PATTERNS = [
  /ResizeObserver loop limit exceeded/i,
  /ResizeObserver loop completed with undelivered notifications/i,
  /^Script error\.?$/,
];

/**
 * Countries where every session is kept, regardless of
 * `NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE`. These are the markets whose
 * behaviour we actually act on, so a 10% sample there loses more than it
 * saves.
 */
const FULLY_SAMPLED_COUNTRIES = new Set(['US', 'NO']);

/**
 * Whether a country (ISO 3166-1 alpha-2) is one we keep every session for.
 *
 * Exported so the identify path can apply the same rule to the authoritative
 * organisation country once it loads — see hooks/useIdentifyMetrics.tsx.
 */
export const isFullySampledCountry = (country: string | null | undefined): boolean =>
  !!country && FULLY_SAMPLED_COUNTRIES.has(country.toUpperCase());

/**
 * Canonical IANA zones for the fully-sampled countries.
 *
 * An explicit set, NOT a prefix match: `America/*` also covers Canada
 * (`America/Toronto`) and Mexico (`America/Mexico_City`), which would quietly
 * turn a targeted decision into "most of the Americas". Norway's mainland zone
 * is `Europe/Oslo`; `Arctic/Longyearbyen` (Svalbard) is a separate identifier
 * for the same offset and is still Norway.
 */
const FULLY_SAMPLED_TIME_ZONES = new Map<string, string>(
  ([
    // Contiguous US, east to west.
    ['America/New_York', 'US'],
    ['America/Detroit', 'US'],
    ['America/Kentucky/Louisville', 'US'],
    ['America/Kentucky/Monticello', 'US'],
    ['America/Indiana/Indianapolis', 'US'],
    ['America/Indiana/Vincennes', 'US'],
    ['America/Indiana/Winamac', 'US'],
    ['America/Indiana/Marengo', 'US'],
    ['America/Indiana/Petersburg', 'US'],
    ['America/Indiana/Vevay', 'US'],
    ['America/Indiana/Tell_City', 'US'],
    ['America/Indiana/Knox', 'US'],
    ['America/Chicago', 'US'],
    ['America/Menominee', 'US'],
    ['America/North_Dakota/Center', 'US'],
    ['America/North_Dakota/New_Salem', 'US'],
    ['America/North_Dakota/Beulah', 'US'],
    ['America/Denver', 'US'],
    ['America/Boise', 'US'],
    ['America/Phoenix', 'US'],
    ['America/Los_Angeles', 'US'],
    // Alaska and Hawaii.
    ['America/Anchorage', 'US'],
    ['America/Juneau', 'US'],
    ['America/Sitka', 'US'],
    ['America/Metlakatla', 'US'],
    ['America/Yakutat', 'US'],
    ['America/Nome', 'US'],
    ['America/Adak', 'US'],
    ['Pacific/Honolulu', 'US'],
    // Norway.
    ['Europe/Oslo', 'NO'],
    ['Arctic/Longyearbyen', 'NO'],
  ] as const).map(([zone, country]) => [zone, country]),
);

/**
 * Best-effort country for the CURRENT page load.
 *
 * Timezone rather than an IP-based country, which only the server sees (the GCP
 * LB's `x-client-region` header): Faro draws a session's sampling verdict inside
 * `initializeFaro`, so anything that needs a round-trip resolves too late to
 * influence it — and a session sampled out at page load stays invisible for
 * its whole lifetime. A synchronous, offline signal is the only kind that can
 * make this decision at all.
 *
 * Inexact by nature (a VPN or a traveller reads as the wrong country). It errs
 * toward under-sampling rather than over-sampling, and
 * `Telemetry().forceFullSession()` remains available for any flow that must
 * not be lost regardless of geography.
 */
const getCountryFromTimeZone = (): string | undefined => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return zone ? FULLY_SAMPLED_TIME_ZONES.get(zone) : undefined;
  } catch {
    // Intl is unavailable or the zone is unresolvable — fall back to the
    // configured rate rather than guessing.
    return undefined;
  }
};

/** The configured baseline, before any country override. */
const getConfiguredSamplingRate = () => {
  const raw = process.env.NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE;
  const parsed = raw ? Number(raw) : 1;
  return Number.isFinite(parsed) ? Math.min(1, Math.max(0, parsed)) : 1;
};

/**
 * Fraction of *sessions* (not events) that send any telemetry at all.
 *
 * 1 in the fully-sampled countries; otherwise
 * `NEXT_PUBLIC_TELEMETRY_SAMPLING_RATE` — still the fastest lever to pull
 * (env var + redeploy, no code change) if the collector gets overloaded.
 */
const getSessionSamplingRate = () => {
  const country = getCountryFromTimeZone();
  if (country && FULLY_SAMPLED_COUNTRIES.has(country)) return 1;
  return getConfiguredSamplingRate();
};

/**
 * Flipped by `forceFullSession()` (see hooks/useTelemetry.tsx) for a session
 * that must not be lost to `getSessionSamplingRate()`.
 *
 * A plain overwrite of the session's `isSampled` attribute isn't enough:
 * Faro's own SessionInstrumentation reconciles the live session against
 * sessionStorage on every `setSession()` call, and whenever it sees the
 * attributes differ from storage it re-draws `isSampled()` from scratch —
 * silently reverting a one-off attribute poke back to the configured rate's
 * verdict. Routing the override through the `sampler` below means that
 * re-draw also sees the override, instead of undoing it.
 */
let forceFullSampling = false;

export const setForceFullSampling = (): void => {
  forceFullSampling = true;
};

/**
 * Exported so tests can wire the exact same sampler into their own Faro
 * instance. Returns the configured rate (i.e. Faro's default behaviour) until
 * the override is flipped on.
 */
export const fullSessionSampler = (): number =>
  forceFullSampling ? 1 : getSessionSamplingRate();

/**
 * Initializes the Faro browser SDK exactly once per page load, replacing the
 * old New Relic Browser agent (`public/newlearning.js` + the inline snippets
 * in `pages/_document.tsx`). Safe to call from multiple components — only
 * the first call does anything.
 *
 * `@grafana/faro-web-sdk` is loaded via dynamic `import()` rather than a
 * static top-level import: every call site that touches telemetry
 * (`hooks/useTelemetry.tsx`, and everything that imports it —
 * `services/backendService`, `frontendServices/gameAPI.ts`, the designer
 * hooks, ...) would otherwise pull the whole SDK — and, transitively, a
 * `web-vitals` sub-dependency shipped as ESM-only — into every module graph
 * that references them, including Jest's CJS test environment, which can't
 * parse it. A dynamic import only loads it when this function actually runs
 * (i.e. in the browser, from `TelemetryInit`), not merely when referenced.
 */
export const initTelemetry = (): void => {
  if (typeof window === 'undefined') return;
  if (faroInstance || initializing) return;
  initializing = true;

  void import('@grafana/faro-web-sdk')
    .then(({ initializeFaro, getWebInstrumentations }) => {
      faroInstance = initializeFaro({
        url: getCollectorProxyUrl(),
        app: {
          name: getFaroAppName(),
          // The human release, from package.json via the deploy workflows
          // (e.g. "11.57.4"). This is what Grafana's release filters and
          // "errors by version" panels group by, so it must NOT be the bundle
          // id — an earlier revision set this to NEXT_PUBLIC_SHA8, which made
          // every release read as an opaque "d-3f2a1b9c".
          version: process.env.NEXT_PUBLIC_VERSION,
          // Source-map correlation key, a separate field from `version`.
          // Matches the `--bundle-id` both faro-cli steps pass in the deploy
          // workflows. Faro can also auto-detect this from the
          // `__faroBundleId_<appName>` global that `inject-bundle-id` writes
          // into the built JS, but setting it explicitly means source maps
          // still correlate if that step (which is `continue-on-error`) fails.
          bundleId: process.env.NEXT_PUBLIC_SHA8,
          environment: getFaroEnvironment(),
        },
        sessionTracking: {
          samplingRate: getSessionSamplingRate(),
          sampler: fullSessionSampler,
        },
        // Faro's own dedupe + ignore-list — no need to hand-roll either.
        dedupe: true,
        ignoreErrors: IGNORED_ERROR_PATTERNS,
        instrumentations: getWebInstrumentations({
          captureConsole: false,
          // Reports one `faro.performance.resource` event per fetch/XHR the
          // app makes. The designer alone fires a lot of those (autosave,
          // socket fallbacks, generator polling) — pure volume on top of our
          // own explicit metrics, without adding signal we currently use.
          // Disabled deliberately; revisit if per-request timing breakdowns
          // become worth the extra events.
          enablePerformanceInstrumentation: false,
        }),
      });

      // Stamp the sampling inputs onto the session so the heuristic can be
      // audited from the data itself rather than trusted. Comparing
      // `session_attr_telemetryCountry` against the org country the app
      // already knows shows how often the timezone guess is wrong, and
      // `session_attr_telemetrySamplingRate` explains why a cohort looks
      // thinner than expected.
      const session = faroInstance.api.getSession();
      faroInstance.api.setSession({
        ...session,
        attributes: {
          ...session?.attributes,
          telemetryCountry: getCountryFromTimeZone() ?? 'unknown',
          telemetrySamplingRate: String(getSessionSamplingRate()),
        },
      });

      drainPreInitErrorQueue(faroInstance);
      drainPendingCalls(faroInstance);
    })
    .catch(error => {
      // Telemetry failing to start must never surface as an unhandled
      // rejection in the app — but it also must not disappear silently, or a
      // broken pipeline looks exactly like "no errors happened".
      // eslint-disable-next-line no-console
      console.warn('Failed to initialize telemetry', error);
    })
    .finally(() => {
      initializing = false;
    });
};

/**
 * The inline script in `pages/_document.tsx` wraps `console.error` to catch
 * React's hydration errors, which never surface as thrown errors Faro's own
 * instrumentation would see. That script runs in <head>, long before this
 * module's dynamic import resolves, so it parks its errors on
 * `window.__curipodTelemetryQueue` and we flush them here — otherwise the
 * earliest (and most interesting) hydration failures of a page load would be
 * the exact ones that got dropped.
 */
const drainPreInitErrorQueue = (faro: Faro) => {
  const queue = window.__curipodTelemetryQueue;
  if (!queue) return;
  queue.splice(0).forEach(error => faro.api.pushError(error));
  // Anything the wrapper pushes from now on goes straight through.
  window.__curipodTelemetryQueue = {
    push: (error: Error) => {
      faro.api.pushError(error);
      return 0;
    },
  } as Error[];
};

export const getFaro = () => faroInstance;
